import React, { useState, useCallback } from 'react';
import SafeIcon from '../common/SafeIcon';
import { generateContent } from '../services/api';
import { useProfile } from '../context/ProfileContext';

const OptionBlock = ({ number, data, onChange }) => (
  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3">
    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
        {number}
      </span> Option {number}
    </h3>
    <div className="grid grid-cols-2 gap-3">
      <input type="text" name="line" placeholder="Cruise Line" value={data.line} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
      <input type="text" name="ship" placeholder="Ship Name" value={data.ship} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
      <input type="text" name="itinerary" placeholder="Itinerary" value={data.itinerary} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
      <input type="text" name="date" placeholder="Sail Date" value={data.date} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
      <input type="text" name="cabin" placeholder="Cabin Category" value={data.cabin} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
      <input type="text" name="price" placeholder="Price Per Person" value={data.price} onChange={(e) => onChange(e, `option${number}`)} className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
    </div>
  </div>
);

const CompareAndPitch = () => {
  const { getSignature, isLimitReached, incrementUsage } = useProfile();
  const [formData, setFormData] = useState({
    clientName: '',
    clientNotes: '',
    option1: { line: '', ship: '', itinerary: '', date: '', cabin: '', price: '' },
    option2: { line: '', ship: '', itinerary: '', date: '', cabin: '', price: '' },
    option3: { line: '', ship: '', itinerary: '', date: '', cabin: '', price: '' }
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  const handleInputChange = useCallback((e, section = null) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (section) return { ...prev, [section]: { ...prev[section], [name]: value } };
      return { ...prev, [name]: value };
    });
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (isLimitReached) return;

    setLoading(true);
    setResult('');
    try {
     const generatedText = await generateContent('compare', formData);
const signature = buildSignature();
setResult(generatedText + '\n\n' + signature);
      incrementUsage();
    } catch (error) {
      setResult("Sorry, an error occurred while generating the comparison. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const buildSignature = () => {
  const profile = JSON.parse(localStorage.getItem('cruisecopilot_agent_profile') || '{}');
  
  const name = (profile.firstName && profile.lastName)
    ? `${profile.firstName} ${profile.lastName}`
    : 'Your Cruise Specialist';
  
  const tagline = profile.tagline || null;
  const agency = profile.agencyName || 'Avoya Travel Network';
  const email = profile.email || null;
  const phone = profile.phone || null;

  const contactLine = [email, phone].filter(Boolean).join(' | ');

  return [
    'Warm regards,',
    name,
    tagline,
    agency,
    contactLine
  ].filter(Boolean).join('\n');
};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-h-[800px] overflow-y-auto">
        <div className="flex items-center gap-2 mb-6">
          <SafeIcon name="Repeat" className="text-blue-600 text-xl" />
          <h2 className="text-xl font-semibold text-slate-800">Comparison Parameters</h2>
        </div>
        <form onSubmit={handleGenerate} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client Name</label>
              <input type="text" name="clientName" required value={formData.clientName} onChange={handleInputChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all" placeholder="e.g., Sarah Jenkins" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client Profile / Notes</label>
              <textarea name="clientNotes" required value={formData.clientNotes} onChange={handleInputChange} rows="2" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm transition-all" placeholder="e.g., Anniversary trip, loves fine dining..." ></textarea>
            </div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((num) => (
              <OptionBlock key={num} number={num} data={formData[`option${num}`]} onChange={handleInputChange} />
            ))}
          </div>
          <button 
            type="submit" 
            disabled={loading || !formData.clientName || !formData.option1.line || isLimitReached} 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm active:scale-[0.98]" 
          >
            {loading ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Zap" />}
            {loading ? 'Analyzing Options...' : isLimitReached ? 'Limit Reached' : 'Generate Pitch'}
          </button>
        </form>
      </div>

      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 flex flex-col h-full min-h-[500px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <SafeIcon name="FileText" className="text-blue-600" /> Client Pitch
          </h2>
          {result && (
            <button onClick={handleCopy} className="text-sm flex items-center gap-1.5 text-slate-600 hover:text-blue-600 transition-all bg-white px-3 py-1.5 rounded-md border border-slate-200 shadow-sm font-medium" >
              {copied ? <SafeIcon name="Check" className="text-green-500" /> : <SafeIcon name="Copy" />}
              {copied ? 'Copied!' : 'Copy Pitch'}
            </button>
          )}
        </div>
        <div className="flex-grow bg-white border border-slate-200 rounded-lg p-6 overflow-y-auto whitespace-pre-wrap text-slate-700 leading-relaxed shadow-inner">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3">
              <SafeIcon name="Search" className="w-10 h-10 animate-pulse text-blue-400" />
              <p className="font-medium">Comparing ships and itineraries...</p>
            </div>
          ) : result ? <div className="max-w-none">{result}</div> : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-center px-8 space-y-4">
              <div className="bg-slate-50 p-4 rounded-full"><SafeIcon name="BarChart2" className="w-8 h-8 opacity-50" /></div>
              <p>{isLimitReached ? 'You have reached your limit of 10 free generations.' : 'Enter cruise options and client details to generate a professional comparison.'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareAndPitch;